import React, { useState, useEffect } from 'react';

const API = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5001`;

/* =========================================================================
   Default colors for sources when the API doesn't provide one
   ========================================================================= */
const DEFAULT_COLORS = ['#F59E0B', '#3B82F6', '#10B981', '#EF4444', '#8B5CF6', '#EC4899'];

function pickColor(source, index) {
  return DEFAULT_COLORS[index % DEFAULT_COLORS.length];
}

/* =========================================================================
   DataInventory — Gantt-style timeline of stored climate datasets
   ========================================================================= */
export default function DataInventory({ locationId, onExtendDataset, refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedDatasets, setExpandedDatasets] = useState({});

  /* ---- Fetch inventory ------------------------------------------------ */
  useEffect(() => {
    if (!locationId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetch(`${API}/api/locations/${locationId}/inventory`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((json) => {
        if (!cancelled) {
          setData(json);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message);
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [locationId, refreshKey]);

  /* ---- Toggle variable rows for a dataset ----------------------------- */
  const toggleExpanded = (datasetId) => {
    setExpandedDatasets((prev) => ({ ...prev, [datasetId]: !prev[datasetId] }));
  };

  /* ---- Compute timeline range ----------------------------------------- */
  const timelineRange = React.useMemo(() => {
    if (!data || !data.datasets || data.datasets.length === 0) return null;
    let minYear = Infinity;
    let maxYear = -Infinity;
    data.datasets.forEach((ds) => {
      if (ds.year_start < minYear) minYear = ds.year_start;
      if (ds.year_end > maxYear) maxYear = ds.year_end;
    });
    // pad one year on each side for visual breathing room
    return { min: minYear - 1, max: maxYear + 1 };
  }, [data]);

  const totalYears = timelineRange ? timelineRange.max - timelineRange.min + 1 : 0;

  /* ---- Helpers -------------------------------------------------------- */
  const yearToPercent = (year) => {
    if (!timelineRange) return 0;
    return ((year - timelineRange.min) / totalYears) * 100;
  };

  const spanPercent = (start, end) => {
    if (!timelineRange) return 0;
    return ((end - start + 1) / totalYears) * 100;
  };

  const formatNumber = (n) => {
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
    if (n >= 1_000) return (n / 1_000).toFixed(1) + 'k';
    return String(n);
  };

  /* ---- Year header ticks ---------------------------------------------- */
  const renderYearHeaders = () => {
    if (!timelineRange) return null;
    const years = [];
    for (let y = timelineRange.min; y <= timelineRange.max; y++) {
      years.push(y);
    }
    // show every year if <= 25, otherwise every 2 or 5
    let step = 1;
    if (totalYears > 40) step = 5;
    else if (totalYears > 25) step = 2;

    return (
      <div style={styles.yearHeaderRow}>
        <div style={styles.labelColumn} />
        <div style={styles.timelineColumn}>
          {years.map((y) => {
            const show = y % step === 0;
            return (
              <div
                key={y}
                style={{
                  position: 'absolute',
                  left: `${yearToPercent(y)}%`,
                  width: `${100 / totalYears}%`,
                  textAlign: 'center',
                  fontSize: 10,
                  color: show ? '#94a3b8' : 'transparent',
                  userSelect: 'none',
                  borderLeft: '1px solid #334155',
                  height: '100%',
                  boxSizing: 'border-box',
                }}
              >
                {show ? y : ''}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  /* ---- Dataset bar ---------------------------------------------------- */
  const renderDatasetBar = (ds, color) => {
    const left = yearToPercent(ds.year_start);
    const width = spanPercent(ds.year_start, ds.year_end);

    return (
      <div style={{ position: 'relative', height: 28 }}>
        {/* Coverage-by-year cells */}
        {ds.coverage_by_year && Object.entries(ds.coverage_by_year).map(([yearStr, info]) => {
          const year = parseInt(yearStr, 10);
          const cellLeft = yearToPercent(year);
          const cellWidth = 100 / totalYears;
          const opacity = info.complete ? 1.0 : 0.45;
          return (
            <div
              key={year}
              title={`${year}: ${info.hours}/${info.expected} hrs${info.complete ? '' : ' (incomplete)'}`}
              style={{
                position: 'absolute',
                left: `${cellLeft}%`,
                width: `${cellWidth}%`,
                top: 2,
                height: 24,
                backgroundColor: color,
                opacity,
                borderRight: '1px solid rgba(0,0,0,0.2)',
                boxSizing: 'border-box',
                borderRadius: 0,
              }}
            />
          );
        })}
        {/* If no coverage_by_year, draw a solid bar */}
        {!ds.coverage_by_year && (
          <div
            style={{
              position: 'absolute',
              left: `${left}%`,
              width: `${width}%`,
              top: 2,
              height: 24,
              backgroundColor: color,
              borderRadius: 3,
            }}
          />
        )}
        {/* Label on bar */}
        <div
          style={{
            position: 'absolute',
            left: `${left}%`,
            width: `${width}%`,
            top: 2,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 11,
            fontWeight: 600,
            color: '#fff',
            textShadow: '0 1px 2px rgba(0,0,0,0.5)',
            pointerEvents: 'none',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          {ds.year_start}&#8211;{ds.year_end} ({formatNumber(ds.row_count)} hrs)
        </div>
      </div>
    );
  };

  /* ---- Variable completeness row -------------------------------------- */
  const renderVariableRow = (varName, info, color) => {
    const pct = info.pct != null ? info.pct : (info.total > 0 ? (info.non_null / info.total) * 100 : 0);
    const barColor = pct >= 99 ? color : pct >= 90 ? '#F59E0B' : '#EF4444';

    return (
      <div key={varName} style={styles.variableRow}>
        <div style={styles.variableLabelCol}>
          <span style={{ fontSize: 11, color: '#94a3b8' }}>{varName}</span>
        </div>
        <div style={styles.variableBarCol}>
          <div style={styles.variableBarTrack}>
            <div
              style={{
                height: '100%',
                width: `${Math.min(pct, 100)}%`,
                backgroundColor: barColor,
                borderRadius: 3,
                transition: 'width 0.3s ease',
              }}
            />
          </div>
          <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 6, minWidth: 40 }}>
            {pct.toFixed(1)}%
          </span>
        </div>
      </div>
    );
  };

  /* ---- Main render ---------------------------------------------------- */
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner} />
          <span style={{ color: '#94a3b8', marginLeft: 10 }}>Loading inventory...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={{ color: '#EF4444', padding: 16 }}>Failed to load inventory: {error}</div>
      </div>
    );
  }

  if (!data || !data.datasets || data.datasets.length === 0) {
    return (
      <div style={styles.container}>
        <div style={{ color: '#94a3b8', padding: 16 }}>No climate datasets stored for this location.</div>
      </div>
    );
  }

  const { datasets, summary } = data;

  return (
    <div style={styles.container}>
      {/* Title */}
      <div style={styles.title}>Data Inventory</div>

      {/* Timeline header */}
      {renderYearHeaders()}

      {/* Dataset rows */}
      {datasets.map((ds, idx) => {
        const color = ds.color || pickColor(ds.source, idx);
        const expanded = !!expandedDatasets[ds.id];

        return (
          <div key={ds.id} style={styles.datasetBlock}>
            {/* Left label + timeline bar */}
            <div style={styles.datasetRow}>
              <div style={styles.labelColumn}>
                <div
                  style={{ ...styles.sourceBadge, backgroundColor: color, cursor: 'pointer' }}
                  onClick={() => toggleExpanded(ds.id)}
                  title="Click to toggle variable details"
                >
                  {ds.source}
                </div>
                <div style={styles.statusText}>
                  {ds.status === 'complete' ? 'Complete' : ds.status}
                </div>
              </div>
              <div style={styles.timelineColumn}>
                {renderDatasetBar(ds, color)}
              </div>
              {/* Extend button */}
              {onExtendDataset && (
                <button
                  style={styles.extendButton}
                  onClick={() => onExtendDataset(ds.id, ds.year_start, ds.year_end)}
                  title="Extend dataset time range"
                >
                  Extend
                </button>
              )}
            </div>

            {/* Variable completeness rows (expandable) */}
            {expanded && ds.variable_completeness && (
              <div style={styles.variablesContainer}>
                {Object.entries(ds.variable_completeness).map(([varName, info]) =>
                  renderVariableRow(varName, info, color)
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Summary */}
      {summary && (
        <div style={styles.summaryBar}>
          <div style={styles.summaryItem}>
            <span style={styles.summaryLabel}>Datasets</span>
            <span style={styles.summaryValue}>{summary.total_datasets}</span>
          </div>
          <div style={styles.summaryItem}>
            <span style={styles.summaryLabel}>Total Hours</span>
            <span style={styles.summaryValue}>{formatNumber(summary.total_hours)}</span>
          </div>
          <div style={styles.summaryItem}>
            <span style={styles.summaryLabel}>Year Range</span>
            <span style={styles.summaryValue}>
              {summary.year_range ? `${summary.year_range[0]}–${summary.year_range[1]}` : '—'}
            </span>
          </div>
          <div style={styles.summaryItem}>
            <span style={styles.summaryLabel}>Variables</span>
            <span style={styles.summaryValue}>
              {summary.all_variables ? summary.all_variables.join(', ') : '—'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

/* =========================================================================
   Styles
   ========================================================================= */
const styles = {
  container: {
    background: '#1e293b',
    borderRadius: 8,
    border: '1px solid #334155',
    padding: 16,
    color: '#e2e8f0',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  title: {
    fontSize: 14,
    fontWeight: 600,
    color: '#e2e8f0',
    marginBottom: 12,
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
  },
  spinner: {
    width: 18,
    height: 18,
    border: '2px solid #334155',
    borderTopColor: '#3B82F6',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },

  /* Row layout: label column + timeline column */
  yearHeaderRow: {
    display: 'flex',
    alignItems: 'flex-end',
    height: 22,
    marginBottom: 4,
  },
  labelColumn: {
    width: 160,
    minWidth: 160,
    flexShrink: 0,
    paddingRight: 8,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  timelineColumn: {
    flex: 1,
    position: 'relative',
    height: '100%',
  },

  /* Dataset block */
  datasetBlock: {
    marginBottom: 8,
    borderBottom: '1px solid #334155',
    paddingBottom: 8,
  },
  datasetRow: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 36,
  },
  sourceBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 600,
    color: '#fff',
    maxWidth: 150,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  statusText: {
    fontSize: 10,
    color: '#64748b',
    marginTop: 2,
  },
  extendButton: {
    marginLeft: 8,
    padding: '3px 10px',
    fontSize: 11,
    fontWeight: 500,
    color: '#e2e8f0',
    background: '#334155',
    border: '1px solid #475569',
    borderRadius: 4,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },

  /* Variable rows */
  variablesContainer: {
    marginLeft: 160,
    marginTop: 4,
    paddingLeft: 8,
    borderLeft: '2px solid #334155',
  },
  variableRow: {
    display: 'flex',
    alignItems: 'center',
    height: 22,
  },
  variableLabelCol: {
    width: 100,
    minWidth: 100,
    flexShrink: 0,
  },
  variableBarCol: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
  },
  variableBarTrack: {
    flex: 1,
    height: 8,
    backgroundColor: '#0f172a',
    borderRadius: 3,
    overflow: 'hidden',
  },

  /* Summary bar */
  summaryBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    marginTop: 12,
    padding: '10px 12px',
    background: '#0f172a',
    borderRadius: 6,
    border: '1px solid #334155',
  },
  summaryItem: {
    display: 'flex',
    flexDirection: 'column',
  },
  summaryLabel: {
    fontSize: 10,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  summaryValue: {
    fontSize: 13,
    fontWeight: 600,
    color: '#e2e8f0',
    marginTop: 2,
  },
};

/* Inject keyframe animation for the spinner */
if (typeof document !== 'undefined') {
  const styleTag = document.getElementById('data-inventory-spin');
  if (!styleTag) {
    const s = document.createElement('style');
    s.id = 'data-inventory-spin';
    s.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
    document.head.appendChild(s);
  }
}
